// src/components/TaxRegimes/TaxRegimeComparison.js
import React, { useState, useEffect } from "react";
import { auth, db } from "../../firebase/config";
import {
  collection,
  query,
  getDocs,
  where
} from "firebase/firestore";
import MainLayout from "../Layout/MainLayout";
import "./TaxRegime.css";

// Importar serviços refatorados - REUTILIZAÇÃO DE CÓDIGO
import {
  loadAvailablePeriods,
  loadTransactionsForPeriod
} from "../../services/transactionRepository";

const TaxRegimeComparison = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [periods, setPeriods] = useState([]);
  const [monthlyRevenue, setMonthlyRevenue] = useState({});
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [last12MonthsRevenue, setLast12MonthsRevenue] = useState(0);
  const [analyzedPeriods, setAnalyzedPeriods] = useState([]);

  // Estado para seleção de regime e anexo
  const [selectedRegime, setSelectedRegime] = useState("simplesNacional");
  const [selectedAnnex, setSelectedAnnex] = useState("1");
  const [folhaPagamento, setFolhaPagamento] = useState(0);
  const [fatorR, setFatorR] = useState(0);

  // Estados para projeção de receita
  const [useProjection, setUseProjection] = useState(false);
  const [projectedRevenue, setProjectedRevenue] = useState(0);
  const [monthsWithData, setMonthsWithData] = useState(0);

  // Estado para armazenar os resultados dos cálculos
  const [taxResults, setTaxResults] = useState({
    simplesNacional: { taxAmount: 0, effectiveRate: 0, monthlyBreakdown: [] },
    lucroPresumido: { taxAmount: 0, effectiveRate: 0, monthlyBreakdown: [] },
    lucroReal: { taxAmount: 0, effectiveRate: 0, monthlyBreakdown: [] }
  });

  // Dados de alíquotas para o Simples Nacional por anexo
  const simplesNacionalRates = {
    "1": [ // Anexo I - Comércio
      { limit: 180000, rate: 0.04, deduction: 0 },
      { limit: 360000, rate: 0.073, deduction: 5940 },
      { limit: 720000, rate: 0.095, deduction: 13860 },
      { limit: 1800000, rate: 0.107, deduction: 22500 },
      { limit: 3600000, rate: 0.143, deduction: 87300 },
      { limit: 4800000, rate: 0.19, deduction: 378000 }
    ],
    "2": [ // Anexo II - Indústria
      { limit: 180000, rate: 0.045, deduction: 0 },
      { limit: 360000, rate: 0.078, deduction: 5940 },
      { limit: 720000, rate: 0.10, deduction: 13860 },
      { limit: 1800000, rate: 0.112, deduction: 22500 },
      { limit: 3600000, rate: 0.147, deduction: 85500 },
      { limit: 4800000, rate: 0.30, deduction: 720000 }
    ],
    "3": [ // Anexo III - Serviços
      { limit: 180000, rate: 0.06, deduction: 0 },
      { limit: 360000, rate: 0.112, deduction: 9360 },
      { limit: 720000, rate: 0.135, deduction: 17640 },
      { limit: 1800000, rate: 0.16, deduction: 35640 },
      { limit: 3600000, rate: 0.21, deduction: 125640 },
      { limit: 4800000, rate: 0.33, deduction: 648000 }
    ],
    "4": [ // Anexo IV - Serviços
      { limit: 180000, rate: 0.045, deduction: 0 },
      { limit: 360000, rate: 0.09, deduction: 8100 },
      { limit: 720000, rate: 0.102, deduction: 12420 },
      { limit: 1800000, rate: 0.14, deduction: 39780 },
      { limit: 3600000, rate: 0.22, deduction: 183780 },
      { limit: 4800000, rate: 0.33, deduction: 828000 }
    ],
    "5": [ // Anexo V - Serviços
      { limit: 180000, rate: 0.155, deduction: 0 },
      { limit: 360000, rate: 0.18, deduction: 4500 },
      { limit: 720000, rate: 0.195, deduction: 9900 },
      { limit: 1800000, rate: 0.205, deduction: 17100 },
      { limit: 3600000, rate: 0.23, deduction: 62100 },
      { limit: 4800000, rate: 0.305, deduction: 540000 }
    ]
  };

  // Dados para Lucro Presumido
  const lucroPresumidoRates = {
    // Bases para cálculo de IRPJ/CSLL
    presumptionBase: {
      comercio: 0.08, // 8% para comércio
      servicos: 0.32, // 32% para serviços
      transporte: 0.16, // 16% para transporte
      servicosHospitalares: 0.08 // 8% para serviços hospitalares
    },
    // Tributos
    irpj: 0.15,
    irpjAdicional: 0.10, // Adicional para lucro > R$ 20.000 por mês
    irpjAdicionalLimit: 20000 * 3, // Limite trimestral
    csll: 0.09,
    csllBase: {
      comercio: 0.12, // 12% para comércio
      servicos: 0.32 // 32% para serviços
    },
    pis: 0.0065,
    cofins: 0.03,
    iss: 0.05 // Valor pode variar de 2% a 5%
  };

  // Dados para Lucro Real
  const lucroRealRates = {
    irpj: 0.15,
    irpjAdicional: 0.10, // Adicional para lucro > R$ 20.000 por mês
    irpjAdicionalLimit: 20000, // Limite mensal
    csll: 0.09,
    pis: 0.0165,
    cofins: 0.076,
    iss: 0.05 // Valor pode variar de 2% a 5%
  };

  // Função para ordenar os meses dos últimos 12 meses (do mais recente para o mais antigo)
  const getOrderedMonths = () => {
    // Use a lista completa de períodos analisados para manter a ordem cronológica correta
    return analyzedPeriods
      .map(period => period.split('-')[1]) // Extrair apenas o mês
      .filter((month, index, self) => self.indexOf(month) === index); // Remover duplicatas
  };

  // Função para lidar com a mudança do toggle de projeção
  const handleProjectionToggle = () => {
    const newProjectionState = !useProjection;
    setUseProjection(newProjectionState);

    // Atualizar o last12MonthsRevenue com base no novo estado
    if (newProjectionState && analyzedPeriods.length < 12 && monthsWithData > 0) {
      setLast12MonthsRevenue(projectedRevenue);
    } else {
      setLast12MonthsRevenue(totalRevenue);
    }

    // Recalcular os impostos com o novo valor de receita
    setTimeout(() => {
      performTaxCalculations();
    }, 0);
  };

  // Função para calcular a receita projetada
  const calculateProjectedRevenue = (monthlyData) => {
    const monthsWithRevenue = Object.values(monthlyData).filter(revenue => revenue > 0);
    const totalMonths = monthsWithRevenue.length;

    if (totalMonths === 0) {
      return 0;
    }

    const totalRevenue = monthsWithRevenue.reduce((sum, revenue) => sum + revenue, 0);
    const monthlyAverage = totalRevenue / totalMonths;
    const annualProjection = monthlyAverage * 12;

    setMonthsWithData(totalMonths);
    return annualProjection;
  };

  // Buscar os dados do usuário e períodos disponíveis
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setLoading(true);

        if (auth.currentUser) {
          setUser(auth.currentUser);

          // Buscar períodos disponíveis
          const availablePeriods = await loadAvailablePeriods();
          setPeriods(availablePeriods);

          // Carregar automaticamente os dados dos últimos 12 meses
          await loadLast12MonthsData(availablePeriods);
        }
      } catch (error) {
        console.error("Erro ao buscar dados iniciais:", error);
        setError("Erro ao carregar dados. Por favor, tente novamente.");
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, []);

  // Calcular o fator R quando a folha de pagamento mudar
  useEffect(() => {
    if (last12MonthsRevenue > 0 && folhaPagamento > 0) {
      const calculatedFatorR = folhaPagamento / last12MonthsRevenue;
      setFatorR(calculatedFatorR);
    } else {
      setFatorR(0);
    }
  }, [folhaPagamento, last12MonthsRevenue]);

  // Atualizar cálculos quando os dados relevantes mudarem
  useEffect(() => {
    if (Object.keys(monthlyRevenue).length > 0) {
      performTaxCalculations();
    }
  }, [monthlyRevenue, selectedAnnex, folhaPagamento, fatorR, selectedRegime, last12MonthsRevenue]);

  // Componente para exibir a opção de projeção
  const ProjectionToggleSection = () => {
    // Verificar se há condições para mostrar a opção de projeção (menos de 12 meses de dados)
    const hasLessThan12Months = analyzedPeriods.length < 12 && monthsWithData > 0;

    if (!hasLessThan12Months) {
      return null; // Não mostrar quando tiver dados completos de 12 meses
    }

    return (
      <div className="projection-toggle-container">
        <div className="projection-info">
          <h3>Empresa com menos de 12 meses de faturamento?</h3>

          <div className="projection-details">
            <div className="projection-stats">
              <span>Meses com dados: <strong>{monthsWithData}</strong></span>
              <span>Receita total: <strong>{formatCurrency(totalRevenue)}</strong></span>
              <span>Média mensal: <strong>{formatCurrency(projectedRevenue / 12)}</strong></span>
              <span>Receita projetada para 12 meses: <strong>{formatCurrency(projectedRevenue)}</strong></span>
            </div>

            <div className="projection-toggle">
              <label className="switch-toggle">
                <input
                  type="checkbox"
                  checked={useProjection}
                  onChange={() => handleProjectionToggle()}
                />
                <span className="slider"></span>
                <span className="toggle-label">
                  Usar projeção para cálculo de tributação
                </span>
              </label>
            </div>

            {useProjection && (
              <div className="projection-explanation">
                <p>
                  <strong>Como funciona a projeção:</strong> Calculamos a média mensal de receita
                  com base nos {monthsWithData} meses com dados ({formatCurrency(projectedRevenue / 12)}/mês)
                  e multiplicamos por 12 para estimar a receita anual ({formatCurrency(projectedRevenue)}).
                </p>
                <p>
                  Esta projeção é usada para determinar a faixa de tributação no Simples Nacional,
                  seguindo a fórmula: <br />
                  <em>Alíquota efetiva = (RBT12 × Alíquota nominal - Parcela a deduzir) ÷ RBT12</em>
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Função para carregar os dados dos últimos 12 meses
  const loadLast12MonthsData = async (availablePeriods) => {
    try {
      setLoading(true);

      if (!availablePeriods || availablePeriods.length === 0) {
        setError("Não há períodos disponíveis para análise.");
        return;
      }

      // Ordenar períodos do mais recente para o mais antigo
      const sortedPeriods = [...availablePeriods].sort((a, b) =>
        b.value.localeCompare(a.value)
      );

      console.log("Todos os períodos disponíveis:", sortedPeriods.map(p => p.value));

      // Pegar apenas os 12 períodos mais recentes (ou menos se não houver 12)
      const periodsToAnalyze = sortedPeriods.slice(0, Math.min(12, sortedPeriods.length));

      console.log("Os 12 períodos mais recentes:", periodsToAnalyze.map(p => p.value));

      // Montar a estrutura para armazenar receitas mensais
      const monthlyData = {};
      let totalRevenueAmount = 0;

      // Armazenar a lista completa de períodos analisados para usar na ordenação
      const analyzedPeriodsList = [];

      // Para cada período, buscar as transações e calcular a receita
      for (const period of periodsToAnalyze) {
        const periodId = period.value;
        const periodParts = periodId.split('-');
        const year = periodParts[0];
        const month = periodParts[1]; // Extrair o mês do período (formato: YYYY-MM)

        // Adicionar à lista de períodos analisados (manter o formato completo YYYY-MM)
        analyzedPeriodsList.push(periodId);

        console.log(`Processando transações para o período ${periodId}`);

        try {
          // Buscar transações para este período
          const { transactions } = await loadTransactionsForPeriod(periodId);

          // Filtrar apenas transações de receita
          let monthRevenue = 0;

          // Mapear transações para receita bruta usando a mesma lógica do Dashboard
          transactions.forEach(transaction => {
            // Considerar apenas transações positivas para receita bruta
            if (transaction.amount > 0) {
              // Verificar se é uma receita pela categoria
              if (transaction.categoryPath &&
                 (transaction.categoryPath.startsWith('RECEITA') ||
                 transaction.groupName === 'RECEITA')) {
                monthRevenue += transaction.amount;
              }
            }
          });

          // Buscar entradas de caixa para o período (como no Dashboard)
          try {
            // ALTERADO: Usar a subcoleção correta
            const cashEntriesQuery = query(
              collection(db, "cashEntries", auth.currentUser.uid, "documents"),
              where("period", "==", periodId)
            );

            const cashEntriesSnapshot = await getDocs(cashEntriesQuery);

            // Processar cada entrada de dinheiro
            cashEntriesSnapshot.forEach(doc => {
              const entry = doc.data();

              // Verificar se a entrada tem categoria e valor
              if (entry.categoryPath && typeof entry.amount === 'number') {
                const pathParts = entry.categoryPath.split('.');

                if (pathParts.length >= 2) {
                  const mainCategory = pathParts[0];

                  // Categorizar com base no grupo principal
                  if (mainCategory === "RECEITA") {
                    monthRevenue += entry.amount;
                  }
                }
              }
            });
          } catch (error) {
            console.error(`Erro ao processar entradas de dinheiro para o período ${periodId}:`, error);
          }

          // Armazenar os dados do mês - IMPORTANTE: armazenar TODOS os meses, mesmo com receita zero
          console.log(`Receita para o período ${periodId}: ${monthRevenue}`);
          monthlyData[month] = monthRevenue;
          if (monthRevenue > 0) {
            totalRevenueAmount += monthRevenue;
          }

        } catch (error) {
          console.error(`Erro ao processar transações para o período ${periodId}:`, error);
          // Garantir que o mês exista no objeto mesmo em caso de erro
          monthlyData[month] = 0;
        }
      }

      console.log("Meses analisados:", analyzedPeriodsList);
      console.log("Meses com receita calculada:", Object.keys(monthlyData).sort());
      console.log("Receita total:", totalRevenueAmount);

      // Calcular a receita projetada (para 12 meses)
      const calculatedProjectedRevenue = calculateProjectedRevenue(monthlyData);
      setProjectedRevenue(calculatedProjectedRevenue);

      // Atualizar estados
      setAnalyzedPeriods(analyzedPeriodsList);
      setMonthlyRevenue(monthlyData);
      setTotalRevenue(totalRevenueAmount);

      // Definir o valor da receita dos últimos 12 meses
      // Se tiver menos de 12 meses e projeção estiver ativada, usa a projeção
      if (analyzedPeriodsList.length < 12 && useProjection) {
        setLast12MonthsRevenue(calculatedProjectedRevenue);
      } else {
        setLast12MonthsRevenue(totalRevenueAmount);
      }

    } catch (error) {
      console.error("Erro ao carregar dados dos últimos 12 meses:", error);
      setError("Não foi possível carregar os dados dos últimos 12 meses.");
    } finally {
      setLoading(false);
    }
  };

  // Função para calcular todos os regimes tributários
  const performTaxCalculations = () => {
    // Objeto para armazenar os resultados
    const results = {
      simplesNacional: calculateSimplesNacional(),
      lucroPresumido: calculateLucroPresumido(),
      lucroReal: calculateLucroReal()
    };

    // Atualizar o estado com os resultados
    setTaxResults(results);
  };

  // Cálculo do Simples Nacional
  const calculateSimplesNacional = () => {
    const monthlyBreakdown = [];
    let totalTax = 0;

    // Obter tabela de alíquotas do anexo selecionado
    const annexRates = simplesNacionalRates[selectedAnnex];

    // Verificar se é necessário mudar de anexo com base no fator R
    let effectiveAnnex = selectedAnnex;
    if (selectedAnnex === "3" && fatorR < 0.28) {
      effectiveAnnex = "5";
    } else if ((selectedAnnex === "5" || selectedAnnex === "4") && fatorR >= 0.28) {
      effectiveAnnex = "3";
    }

    // Se anexo efetivo for diferente, usar as alíquotas desse anexo
    const effectiveRates = simplesNacionalRates[effectiveAnnex];

    // Garantir que todos os meses dos últimos 12 meses sejam considerados
    const allMonths = Object.keys(monthlyRevenue);

    for (const month of allMonths) {
      const revenue = monthlyRevenue[month];

      if (revenue > 0) {
        // Encontrar a faixa de alíquota com base na receita acumulada dos últimos 12 meses
        let selectedBracket = effectiveRates[0];

        for (const bracket of effectiveRates) {
          if (last12MonthsRevenue <= bracket.limit) {
            selectedBracket = bracket;
            break;
          }
        }

        // Calcular alíquota efetiva
        const effectiveRate = (last12MonthsRevenue * selectedBracket.rate - selectedBracket.deduction) / last12MonthsRevenue;

        // Calcular o imposto
        const tax = revenue * effectiveRate;
        totalTax += tax;

        // Adicionar ao detalhamento mensal
        monthlyBreakdown.push({
          month,
          revenue,
          tax,
          effectiveRate,
          details: {
            bracket: selectedBracket,
            annexUsed: effectiveAnnex
          }
        });
      } else {
        // Mês sem receita
        monthlyBreakdown.push({
          month,
          revenue: 0,
          tax: 0,
          effectiveRate: 0,
          details: {}
        });
      }
    }

    // Calcular alíquota efetiva total
    const effectiveRate = totalRevenue > 0 ? totalTax / totalRevenue : 0;

    return {
      taxAmount: totalTax,
      effectiveRate,
      monthlyBreakdown,
      annexUsed: effectiveAnnex,
      isAnnexChanged: selectedAnnex !== effectiveAnnex
    };
  };

  // Cálculo do Lucro Presumido
  const calculateLucroPresumido = () => {
    const monthlyBreakdown = [];
    let totalTax = 0;

    // Para cada trimestre, calcular o imposto
    const quarters = [
      ['01', '02', '03'], // 1º Trimestre
      ['04', '05', '06'], // 2º Trimestre
      ['07', '08', '09'], // 3º Trimestre
      ['10', '11', '12']  // 4º Trimestre
    ];

    for (const quarter of quarters) {
      let quarterlyRevenue = 0;

      // Calcular receita do trimestre
      for (const month of quarter) {
        if (monthlyRevenue[month]) {
          quarterlyRevenue += monthlyRevenue[month];
        }
      }

      // Cálculos mensais em cada trimestre
      for (const month of quarter) {
        const revenue = monthlyRevenue[month] || 0;

        // PIS e COFINS são calculados mensalmente
        const pis = revenue * lucroPresumidoRates.pis;
        const cofins = revenue * lucroPresumidoRates.cofins;
        const iss = revenue * lucroPresumidoRates.iss;

        // IRPJ e CSLL são calculados trimestralmente, mas vamos pro-rata por mês
        const monthRatio = revenue / (quarterlyRevenue || 1); // Evitar divisão por zero

        // Base presumida (considerando comércio como padrão)
        const irpjBase = quarterlyRevenue * lucroPresumidoRates.presumptionBase.comercio;
        const csllBase = quarterlyRevenue * lucroPresumidoRates.csllBase.comercio;

        // IRPJ trimestral
        let irpjQuarterly = irpjBase * lucroPresumidoRates.irpj;

        // Adicional de IRPJ se base > R$ 60.000 no trimestre
        if (irpjBase > lucroPresumidoRates.irpjAdicionalLimit) {
          irpjQuarterly += (irpjBase - lucroPresumidoRates.irpjAdicionalLimit) * lucroPresumidoRates.irpjAdicional;
        }

        // CSLL trimestral
        const csllQuarterly = csllBase * lucroPresumidoRates.csll;

        // Proporcional para o mês
        const irpj = irpjQuarterly * monthRatio;
        const csll = csllQuarterly * monthRatio;

        // Total de impostos para o mês
        const tax = pis + cofins + iss + irpj + csll;
        totalTax += tax;

        // Detalhamento mensal
        monthlyBreakdown.push({
          month,
          revenue,
          tax,
          effectiveRate: revenue > 0 ? tax / revenue : 0,
          details: {
            pis,
            cofins,
            iss,
            irpj,
            csll,
            irpjBase: irpjBase * monthRatio,
            csllBase: csllBase * monthRatio
          }
        });
      }
    }

    // Calcular alíquota efetiva total
    const effectiveRate = totalRevenue > 0 ? totalTax / totalRevenue : 0;

    return {
      taxAmount: totalTax,
      effectiveRate,
      monthlyBreakdown
    };
  };

  // Cálculo do Lucro Real
  const calculateLucroReal = () => {
    const monthlyBreakdown = [];
    let totalTax = 0;

    // Para este exemplo, assumimos que as despesas são 60% da receita
    const expenseRatio = 0.6;

    // Para cada mês, calcular o imposto
    const months = Object.keys(monthlyRevenue);

    for (const month of months) {
      const revenue = monthlyRevenue[month] || 0;
      const expenses = revenue * expenseRatio;
      const profit = revenue - expenses;

      // PIS e COFINS (podem ter créditos deduzidos)
      // Para simplificar, consideramos um crédito médio de 30% do valor devido
      const pisGross = revenue * lucroRealRates.pis;
      const pisCredit = pisGross * 0.3;
      const pis = pisGross - pisCredit;

      const cofinsGross = revenue * lucroRealRates.cofins;
      const cofinsCredit = cofinsGross * 0.3;
      const cofins = cofinsGross - cofinsCredit;

      // ISS não tem crédito
      const iss = revenue * lucroRealRates.iss;

      // IRPJ e CSLL são calculados sobre o lucro real
      let irpj = 0;
      let irpjAdicional = 0;
      let csll = 0;

      if (profit > 0) {
        irpj = profit * lucroRealRates.irpj;

        // Adicional IRPJ se lucro > R$ 20.000 no mês
        if (profit > lucroRealRates.irpjAdicionalLimit) {
          irpjAdicional = (profit - lucroRealRates.irpjAdicionalLimit) * lucroRealRates.irpjAdicional;
        }

        csll = profit * lucroRealRates.csll;
      }

      // Total de impostos para o mês
      const tax = pis + cofins + iss + irpj + irpjAdicional + csll;
      totalTax += tax;

      // Detalhamento mensal
      monthlyBreakdown.push({
        month,
        revenue,
        tax,
        effectiveRate: revenue > 0 ? tax / revenue : 0,
        details: {
          expenses,
          profit,
          pis,
          pisGross,
          pisCredit,
          cofins,
          cofinsGross,
          cofinsCredit,
          iss,
          irpj,
          irpjAdicional,
          csll
        }
      });
    }

    // Calcular alíquota efetiva total
    const effectiveRate = totalRevenue > 0 ? totalTax / totalRevenue : 0;

    return {
      taxAmount: totalTax,
      effectiveRate,
      monthlyBreakdown
    };
  };

  // Formatador de valores monetários
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  // Formatador de percentuais
  const formatPercent = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'percent',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  // Tratamento de alteração do regime
  const handleRegimeChange = (e) => {
    setSelectedRegime(e.target.value);
  };

  // Tratamento de alteração do anexo
  const handleAnnexChange = (e) => {
    setSelectedAnnex(e.target.value);
  };

  // Tratamento de alteração da folha de pagamento
  const handleFolhaPagamentoChange = (e) => {
    const value = parseFloat(e.target.value.replace(/[^\d]/g, '')) || 0;
    setFolhaPagamento(value);
  };

  // Obter nome do mês
  const getMonthName = (month) => {
    const monthNames = [
      "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
      "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"
    ];
    return monthNames[parseInt(month) - 1];
  };

  return (
      <MainLayout userName={user?.displayName || "Usuário"}>
        <div className="tax-regime-container">
          <div className="tax-regime-header">
            <h1>Comparativo de Regimes Tributários</h1>
          </div>

          {error && <div className="error-message">{error}</div>}
          {success && <div className="success-message">{success}</div>}

          {loading ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Carregando dados fiscais...</p>
            </div>
          ) : (
            <>
              <div className="tax-regime-summary">
                <div className="summary-card">
                  <h3>Receita Bruta (Últimos 12 meses)
                    {useProjection && <span className="projection-active-badge">Projeção ativa</span>}
                  </h3>
                  <div className="summary-value">
                    {formatCurrency(useProjection && analyzedPeriods.length < 12
                      ? projectedRevenue
                      : totalRevenue
                    )}
                  </div>
                  <p className="summary-description">
                    {useProjection && analyzedPeriods.length < 12
                      ? `Projeção baseada em ${monthsWithData} meses de dados`
                      : "Total dos últimos 12 meses"
                    }
                  </p>
                </div>

                {fatorR > 0 && (
                  <div className="summary-card">
                    <h3>Fator R</h3>
                    <div className="summary-value">{formatPercent(fatorR)}</div>
                    <p className="summary-description">
                      {fatorR >= 0.28 ? "Permite enquadramento no Anexo III" : "Inferior a 28%, enquadra no Anexo V"}
                    </p>
                  </div>
                )}
              </div>

              {/* Componente de projeção simplificado */}
              {analyzedPeriods.length < 12 && monthsWithData > 0 && (
                <div className="projection-toggle-container">
                  <div className="projection-toggle">
                    <label className="switch-toggle">
                      <input
                        type="checkbox"
                        checked={useProjection}
                        onChange={() => handleProjectionToggle()}
                      />
                      <span className="slider"></span>
                      <span className="toggle-label">
                        Usar projeção para cálculo de tributação
                      </span>
                    </label>

                    {useProjection && (
                      <div className="projection-explanation">
                        <p>
                          Média mensal de receita: <strong>{formatCurrency(projectedRevenue / 12)}</strong> × 12 meses =
                          Receita anual projetada: <strong>{formatCurrency(projectedRevenue)}</strong>
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="tax-regime-parameters">
              <div className="parameters-card">
                <h3>Parâmetros de Cálculo</h3>

                <div className="parameter-group">
                  <label htmlFor="regime-select">Regime de Interesse:</label>
                  <select
                    id="regime-select"
                    value={selectedRegime}
                    onChange={handleRegimeChange}
                  >
                    <option value="simplesNacional">Simples Nacional</option>
                    <option value="lucroPresumido">Lucro Presumido</option>
                    <option value="lucroReal">Lucro Real</option>
                  </select>
                </div>

                {selectedRegime === "simplesNacional" && (
                  <>
                    <div className="parameter-group">
                      <label htmlFor="annex-select">Anexo do Simples Nacional:</label>
                      <select
                        id="annex-select"
                        value={selectedAnnex}
                        onChange={handleAnnexChange}
                      >
                        <option value="1">Anexo I - Comércio</option>
                        <option value="2">Anexo II - Indústria</option>
                        <option value="3">Anexo III - Serviços</option>
                        <option value="4">Anexo IV - Serviços</option>
                        <option value="5">Anexo V - Serviços</option>
                      </select>
                    </div>

                    {(selectedAnnex === "3" || selectedAnnex === "4" || selectedAnnex === "5") && (
                      <div className="parameter-group">
                        <label htmlFor="folha-pagamento">Folha de Pagamento (12 meses):</label>
                        <input
                          id="folha-pagamento"
                          type="text"
                          value={formatCurrency(folhaPagamento)}
                          onChange={handleFolhaPagamentoChange}
                          placeholder="R$ 0,00"
                        />
                        <p className="parameter-help">
                          Para empresas de serviços, o Fator R (Folha ÷ Receita) pode alterar o anexo aplicável.
                        </p>
                      </div>
                    )}
                  </>
                )}

                {selectedRegime === "lucroPresumido" && (
                  <div className="parameter-info">
                    <p>O cálculo do Lucro Presumido considera:</p>
                    <ul>
                      <li>Base de presunção de 8% para comércio (IRPJ)</li>
                      <li>Base de presunção de 12% para comércio (CSLL)</li>
                      <li>IRPJ: 15% + adicional de 10% para lucro trimestral &gt; R$ 60.000</li>
                      <li>CSLL: 9%</li>
                      <li>PIS: 0,65%</li>
                      <li>COFINS: 3%</li>
                      <li>ISS: 5% (pode variar conforme município)</li>
                    </ul>
                  </div>
                )}

                {selectedRegime === "lucroReal" && (
                  <div className="parameter-info">
                    <p>O cálculo do Lucro Real considera:</p>
                    <ul>
                      <li>Receitas deduzidas de despesas (estimadas em 60% da receita)</li>
                      <li>IRPJ: 15% + adicional de 10% para lucro mensal &gt; R$ 20.000</li>
                      <li>CSLL: 9%</li>
                      <li>PIS: 1,65% (com créditos estimados)</li>
                      <li>COFINS: 7,6% (com créditos estimados)</li>
                      <li>ISS: 5% (pode variar conforme município)</li>
                    </ul>
                  </div>
                )}
              </div>
            </div>

            <div className="tax-regimes-comparison">
              <h2>Comparativo entre Regimes Tributários</h2>

              <div className="regimes-cards">
                <div className={`regime-card ${selectedRegime === 'simplesNacional' ? 'selected' : ''}`}>
                  <h3>Simples Nacional</h3>
                  <div className="regime-tax-value">{formatCurrency(taxResults.simplesNacional.taxAmount)}</div>
                  <div className="regime-tax-rate">
                    Alíquota Efetiva: {formatPercent(taxResults.simplesNacional.effectiveRate)}
                  </div>

                  {taxResults.simplesNacional.isAnnexChanged && (
                    <div className="regime-annex-info">
                      <p>
                        <strong>Anexo utilizado: {taxResults.simplesNacional.annexUsed}</strong><br />
                        O anexo foi alterado devido ao Fator R ({formatPercent(fatorR)})
                      </p>
                    </div>
                  )}
                </div>

                <div className={`regime-card ${selectedRegime === 'lucroPresumido' ? 'selected' : ''}`}>
                  <h3>Lucro Presumido</h3>
                  <div className="regime-tax-value">{formatCurrency(taxResults.lucroPresumido.taxAmount)}</div>
                  <div className="regime-tax-rate">
                    Alíquota Efetiva: {formatPercent(taxResults.lucroPresumido.effectiveRate)}
                  </div>
                </div>

                <div className={`regime-card ${selectedRegime === 'lucroReal' ? 'selected' : ''}`}>
                  <h3>Lucro Real</h3>
                  <div className="regime-tax-value">{formatCurrency(taxResults.lucroReal.taxAmount)}</div>
                  <div className="regime-tax-rate">
                    Alíquota Efetiva: {formatPercent(taxResults.lucroReal.effectiveRate)}
                  </div>
                </div>
              </div>

              <div className="economy-analysis">
                <h3>Análise de Economia Tributária</h3>

                {(() => {
                  // Encontrar o regime com menor carga tributária
                  const regimes = [
                    { name: "Simples Nacional", amount: taxResults.simplesNacional.taxAmount },
                    { name: "Lucro Presumido", amount: taxResults.lucroPresumido.taxAmount },
                    { name: "Lucro Real", amount: taxResults.lucroReal.taxAmount }
                  ];

                  regimes.sort((a, b) => a.amount - b.amount);

                  const bestRegime = regimes[0];
                  const worstRegime = regimes[2];
                  const potentialSavings = worstRegime.amount - bestRegime.amount;

                  return (
                    <div className="economy-summary">
                      <p>
                        O regime tributário mais vantajoso é o <strong>{bestRegime.name}</strong>
                        {potentialSavings > 0 && `, com uma economia potencial de até ${formatCurrency(potentialSavings)} em relação ao ${worstRegime.name}`}.
                        {useProjection && analyzedPeriods.length < 12 &&
                          <span className="projection-note"> (cálculo baseado na receita projetada)</span>
                        }
                      </p>
                    </div>
                  );
                })()}
              </div>
            </div>

            <div className="monthly-breakdown">
              <h2>Detalhamento Mensal</h2>

              <div className="monthly-tabs">
                <button
                  className={selectedRegime === 'simplesNacional' ? 'active' : ''}
                  onClick={() => setSelectedRegime('simplesNacional')}
                >
                  Simples Nacional
                </button>
                <button
                  className={selectedRegime === 'lucroPresumido' ? 'active' : ''}
                  onClick={() => setSelectedRegime('lucroPresumido')}
                >
                  Lucro Presumido
                </button>
                <button
                  className={selectedRegime === 'lucroReal' ? 'active' : ''}
                  onClick={() => setSelectedRegime('lucroReal')}
                >
                  Lucro Real
                </button>
              </div>

              <div className="breakdown-table-container">
                <table className="breakdown-table">
                  <thead>
                    <tr>
                      <th>Mês</th>
                      <th>Receita</th>
                      <th>Imposto</th>
                      <th>Alíquota Efetiva</th>
                      {selectedRegime === 'simplesNacional' && <th>Anexo</th>}
                      {selectedRegime === 'lucroPresumido' && (
                        <>
                          <th>IRPJ</th>
                          <th>CSLL</th>
                          <th>PIS/COFINS</th>
                          <th>ISS</th>
                        </>
                      )}
                      {selectedRegime === 'lucroReal' && (
                        <>
                          <th>Lucro</th>
                          <th>IRPJ+CSLL</th>
                          <th>PIS/COFINS</th>
                          <th>ISS</th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {selectedRegime === 'simplesNacional' &&
                      (() => {
                        // Obter os meses ordenados do mais recente para o mais antigo
                        const orderedMonths = getOrderedMonths();

                        // Criar um mapa para acesso rápido aos dados por mês
                        const breakdownMap = {};
                        taxResults.simplesNacional.monthlyBreakdown.forEach(item => {
                          breakdownMap[item.month] = item;
                        });

                        // Renderizar os meses com dados reais em ordem (do mais recente para o mais antigo)
                        return orderedMonths.map(month => {
                          const item = breakdownMap[month] || {
                            month,
                            revenue: 0,
                            tax: 0,
                            effectiveRate: 0,
                            details: {}
                          };

                          return (
                            <tr key={`simples-${month}`}>
                              <td>{getMonthName(month)}</td>
                              <td>{formatCurrency(item.revenue)}</td>
                              <td>{formatCurrency(item.tax)}</td>
                              <td>{formatPercent(item.effectiveRate)}</td>
                              <td>{item.details.annexUsed || taxResults.simplesNacional.annexUsed || selectedAnnex}</td>
                            </tr>
                          );
                        });
                      })()
                    }

                    {selectedRegime === 'lucroPresumido' &&
                      (() => {
                        // Obter os meses ordenados do mais recente para o mais antigo
                        const orderedMonths = getOrderedMonths();

                        // Criar um mapa para acesso rápido aos dados por mês
                        const breakdownMap = {};
                        taxResults.lucroPresumido.monthlyBreakdown.forEach(item => {
                          breakdownMap[item.month] = item;
                        });

                        // Renderizar os meses com dados reais em ordem (do mais recente para o mais antigo)
                        return orderedMonths.map(month => {
                          const item = breakdownMap[month] || {
                            month,
                            revenue: 0,
                            tax: 0,
                            effectiveRate: 0,
                            details: {}
                          };

                          return (
                            <tr key={`presumido-${month}`}>
                              <td>{getMonthName(month)}</td>
                              <td>{formatCurrency(item.revenue)}</td>
                              <td>{formatCurrency(item.tax)}</td>
                              <td>{formatPercent(item.effectiveRate)}</td>
                              <td>{formatCurrency(item.details.irpj || 0)}</td>
                              <td>{formatCurrency(item.details.csll || 0)}</td>
                              <td>{formatCurrency((item.details.pis || 0) + (item.details.cofins || 0))}</td>
                              <td>{formatCurrency(item.details.iss || 0)}</td>
                            </tr>
                          );
                        });
                      })()
                    }

                    {selectedRegime === 'lucroReal' &&
                      (() => {
                        // Obter os meses ordenados do mais recente para o mais antigo
                        const orderedMonths = getOrderedMonths();

                        // Criar um mapa para acesso rápido aos dados por mês
                        const breakdownMap = {};
                        taxResults.lucroReal.monthlyBreakdown.forEach(item => {
                          breakdownMap[item.month] = item;
                        });

                        // Renderizar os meses com dados reais em ordem (do mais recente para o mais antigo)
                        return orderedMonths.map(month => {
                          const item = breakdownMap[month] || {
                            month,
                            revenue: 0,
                            tax: 0,
                            effectiveRate: 0,
                            details: {}
                          };

                          return (
                            <tr key={`real-${month}`}>
                              <td>{getMonthName(month)}</td>
                              <td>{formatCurrency(item.revenue)}</td>
                              <td>{formatCurrency(item.tax)}</td>
                              <td>{formatPercent(item.effectiveRate)}</td>
                              <td>{formatCurrency(item.details.profit || 0)}</td>
                              <td>{formatCurrency((item.details.irpj || 0) + (item.details.irpjAdicional || 0) + (item.details.csll || 0))}</td>
                              <td>{formatCurrency((item.details.pis || 0) + (item.details.cofins || 0))}</td>
                              <td>{formatCurrency(item.details.iss || 0)}</td>
                            </tr>
                          );
                        });
                      })()
                    }
                  </tbody>
                  <tfoot>
                    <tr className="total-row">
                      <td><strong>TOTAL</strong></td>
                      <td>{formatCurrency(totalRevenue)}</td>
                      <td>{formatCurrency(taxResults[selectedRegime].taxAmount)}</td>
                      <td>{formatPercent(taxResults[selectedRegime].effectiveRate)}</td>
                      {selectedRegime === 'simplesNacional' && <td>-</td>}
                      {selectedRegime === 'lucroPresumido' && (
                        <>
                          <td>-</td>
                          <td>-</td>
                          <td>-</td>
                          <td>-</td>
                        </>
                      )}
                      {selectedRegime === 'lucroReal' && (
                        <>
                          <td>-</td>
                          <td>-</td>
                          <td>-</td>
                          <td>-</td>
                        </>
                      )}
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
            
            {/* Seção de Recomendações Fiscais removida */}

          </>
        )}
      </div>
    </MainLayout>
  );
};

export default TaxRegimeComparison;